// client/src/utils/captureFix.js
//
// HOW A POSITION IS READ — the sampling half, shared with the phone.
//
// The policy next door in geofence.js was already shared and correct while this
// was copied out four times, and the copy on the web's own register had an 8s
// window and no two-sample floor. That is what told a teacher standing on the
// campus they were a kilometre away, every time rather than occasionally.
//
// The platform half is NOT here — a single module cannot import expo-location
// and also touch navigator. See client/src/utils/browserLocation.js for the
// thirty lines this needs from a browser.
//
// GENERATED — do not edit by hand.
//
// Copied from packages/core/src/geo/captureFix.js by tools/sync-geofence.py. Edit the core
// file and re-run that script. `node tools/geofence-conformance.mjs` asserts
// this copy still behaves identically to it.

import {
  betterFix,
  fixIsGoodEnough,
  fixIsUsableAsCentre,
  geofenceVerdict,
  haversineDistance,
  FIX_WINDOW_MS,
  GOOD_FIX_METRES,
  MIN_SAMPLES,
  TARGET_ACCURACY_M,
  USELESS_ACCURACY_M,
} from './geofence.js';

/**
 * How many identical samples before a stream is called a network estimate.
 *
 * Two is not enough — a GNSS receiver parked on a strong fix can repeat itself
 * once. Three identical readings in a row does not happen with satellites.
 */
export const NETWORK_SIGNATURE_SAMPLES = 3;

/** A fresh, empty sampling state. */
export function createFixState() {
  return {
    best: null,
    first: null,
    samples: 0,
    // The largest distance in metres between any sample and the first one.
    // Zero after several samples is the signature of a network estimate.
    spread: 0,
    // Set once any sample lands somewhere different from the first. Before
    // that, everything seen so far might be one cached estimate repeated.
    moved: false,
  };
}

/**
 * Where a stream's readings are coming from, judged by their behaviour.
 *
 * A GNSS receiver never returns the same coordinate twice: the solution jitters
 * at the 1e-6-degree level between fixes even on a tripod. A wifi or cell
 * estimate is a database lookup — it returns the SAME coordinate with the SAME
 * accuracy, sample after sample, because nothing about it is being measured.
 *
 * That difference is observable without any platform API telling us, which
 * matters because neither expo-location nor the browser will say which provider
 * answered.
 *
 * @returns {'gnss'|'network'|'unknown'}
 */
export function fixSource(state) {
  if (state.moved) return 'gnss';
  if (state.samples >= NETWORK_SIGNATURE_SAMPLES) return 'network';
  return 'unknown';
}

/**
 * Fold one reading into the state and say whether sampling can stop.
 *
 * PURE, and deliberately so: this is every rule the four copies disagreed
 * about, in one function with no timers, no platform and no promises, so the
 * conformance script can replay a scripted sequence of samples through both
 * copies and compare them step for step.
 *
 * @param {object} state    from createFixState, treated as immutable
 * @param {{latitude:number, longitude:number, accuracy:number}} sample
 * @param {object} [options]
 * @param {'check'|'centre'} [options.purpose]
 *        `centre` is the school's surveyed point. It is held to absolute
 *        precision because there is no fence yet for it to be unambiguous
 *        against, and because one bad centre moves the fence for every teacher
 *        every day, where one bad check is one register.
 * @param {{radius:number, latitude:number, longitude:number}} [options.target]
 *        the fence being tested. Given one, a `check` can stop as soon as the
 *        reading is unambiguously INSIDE it.
 * @returns {{state:object, done:boolean, reason:string}}
 */
export function considerSample(state, sample, options = {}) {
  const purpose = options.purpose === 'centre' ? 'centre' : 'check';
  const target = options.target || null;

  const lat = Number(sample?.latitude);
  const lon = Number(sample?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return { state, done: false, reason: 'not-a-position' };
  }
  const fix = {
    latitude: lat,
    longitude: lon,
    accuracy: Number.isFinite(Number(sample.accuracy)) ? Number(sample.accuracy) : 0,
  };

  const first = state.first || fix;
  const offset = state.first
    ? haversineDistance(first.latitude, first.longitude, fix.latitude, fix.longitude)
    : 0;
  const moved = state.moved || offset > 0;

  // WHICH FIX IS "BEST" DEPENDS ON WHETHER ANYTHING HAS MOVED YET.
  //
  // `betterFix` ranks by REPORTED accuracy, and a wifi estimate claiming ±25m
  // beats a genuine satellite fix reporting ±30m — so without this the whole
  // window could be spent holding on to the wrong answer, and the two-sample
  // floor does not help because the estimate repeats itself identically.
  //
  // So the moment a stream demonstrates it is really measuring something, every
  // reading from before that point is discarded and the ranking starts again
  // from the sample that proved it. A network estimate can no longer win on a
  // number it made up about itself.
  const startingOver = moved && !state.moved;
  const best = startingOver ? fix : betterFix(state.best, fix);

  const next = {
    best,
    first,
    samples: state.samples + 1,
    spread: Math.max(state.spread, offset),
    moved,
  };

  const stop = (reason) => ({ state: next, done: true, reason });
  const go = (reason) => ({ state: next, done: false, reason });

  // NEVER ANSWER FROM THE FIRST SAMPLE. See MIN_SAMPLES in geofence.js.
  if (next.samples < MIN_SAMPLES) return go('too-few-samples');

  // A reading this vague says nothing about any sane fence and will not improve
  // by waiting — it means no satellites are visible at all.
  if (!(best.accuracy > 0) || best.accuracy > USELESS_ACCURACY_M) return go('too-vague');

  if (purpose === 'centre') {
    // No fence to be unambiguous against, so only absolute precision will do —
    // AND the stream has to have proved it is really measuring something.
    //
    // Precision alone is not enough here, and this is the case that matters
    // most: a desktop browser answers from a wifi database with a flattering
    // ±6m, which sails past any accuracy bar, and that coordinate then becomes
    // the school's centre and moves the fence for every teacher every day. A
    // centre is written down permanently, so it waits for the jitter that only
    // a real receiver produces.
    //
    // The cost is a stream that genuinely never moves being made to serve the
    // whole window and then refused. Across three or more readings that does
    // not happen with satellites, and the fields stay editable either way — the
    // recoverable failure is the right one to risk for a value this load-bearing.
    if (!next.moved) return go('centre-needs-a-real-receiver');
    return best.accuracy <= GOOD_FIX_METRES ? stop('precise') : go('centre-needs-precision');
  }

  if (!target || !Number.isFinite(Number(target.latitude))
    || !Number.isFinite(Number(target.longitude))) {
    // Nothing to measure against, so precision is the only stopping rule.
    return best.accuracy <= GOOD_FIX_METRES ? stop('precise') : go('no-target');
  }

  const distance = haversineDistance(
    best.latitude, best.longitude, Number(target.latitude), Number(target.longitude),
  );
  const { verdict } = geofenceVerdict({
    distance, accuracy: best.accuracy, radius: target.radius,
  });

  // EARLY EXIT ONLY ON `inside`.
  //
  // This is the rule the web register did not have, and the asymmetry is the
  // whole point. An error circle that lies WHOLLY INSIDE the fence cannot be
  // wrong in the direction that hurts: whatever the true position is, it is on
  // school grounds, so stopping early costs nothing and saves the teacher
  // fifteen seconds at the start of every register.
  //
  // Concluding `outside` REFUSES A REGISTER. That decision has to be earned by
  // the whole window, because the readings that arrive early are exactly the
  // ones that are not measurements — and during those fifteen seconds the real
  // satellite fix arrives and replaces them. A teacher genuinely off-campus
  // waits fifteen seconds once; a teacher standing in their own classroom is
  // not turned away on the strength of a wifi lookup.
  if (verdict === 'inside' && fixIsGoodEnough({ accuracy: best.accuracy, distance, radius: target.radius })) {
    return stop('inside');
  }

  return go(verdict === 'outside' ? 'outside-must-serve-window' : 'uncertain');
}

/**
 * Read the best position obtainable within the shared window.
 *
 * @param {{watch:Function, oneShot?:Function}} provider
 *        `watch(onSample, onError)` returns a stop function. `oneShot()`
 *        returns a promise of a single sample, used only when the stream
 *        cannot be started at all.
 * @param {object} [options]
 * @param {{radius:number, latitude:number, longitude:number}} [options.target]
 * @param {(progress:object) => void} [options.onProgress]
 *        called as readings improve, so a panel can tighten live rather than
 *        freezing on "checking" for the whole window
 * @param {'check'|'centre'} [options.purpose]
 * @param {number} [options.windowMs]
 * @returns {Promise<{latitude:number, longitude:number, accuracy:number,
 *                    samples:number, spread:number, settled:boolean,
 *                    source:string, capturedAt:number}>}
 */
export function captureBestFix(provider, options = {}) {
  const {
    target = null,
    onProgress = null,
    purpose = 'check',
    windowMs = FIX_WINDOW_MS,
  } = options;

  return new Promise((resolve, reject) => {
    let state = createFixState();
    let stopWatch = null;
    let timer = null;
    let done = false;

    const finish = (settled) => {
      if (done) return;
      done = true;
      // Exactly once, on every path — settle, timeout, error, no-fix. A leaked
      // watch keeps the GPS chip warm for the rest of the session and nobody
      // ever reports it as a bug, they report the battery.
      if (stopWatch) { try { stopWatch(); } catch (_) { /* already gone */ } stopWatch = null; }
      if (timer) { clearTimeout(timer); timer = null; }

      if (!state.best) { reject(new Error('no position')); return; }
      resolve({
        ...state.best,
        samples: state.samples,
        spread: state.spread,
        settled,
        source: fixSource(state),
        capturedAt: Date.now(),
      });
    };

    timer = setTimeout(() => finish(false), windowMs);

    const onSample = (sample) => {
      if (done) return;
      const step = considerSample(state, sample, { purpose, target });
      state = step.state;
      if (onProgress && state.best) {
        // Display only — a throw in someone's render must not kill the fix.
        try {
          onProgress({
            ...state.best,
            samples: state.samples,
            spread: state.spread,
            source: fixSource(state),
          });
        } catch (_) { /* display only */ }
      }
      if (step.done) finish(true);
    };

    // A reading already in hand beats the error: the stream can fail after
    // several good fixes, and throwing those away helps nobody. With nothing in
    // hand, `finish` rejects — which is the honest answer and what the callers
    // are written to expect.
    const onError = () => finish(false);

    let started;
    try {
      started = provider.watch(onSample, onError);
    } catch (_) {
      started = null;
    }

    const attachStop = (stop) => {
      if (typeof stop !== 'function') return;
      if (done) { try { stop(); } catch (_) { /* raced the timeout */ } return; }
      stopWatch = stop;
    };

    if (started && typeof started.then === 'function') {
      started.then(attachStop).catch(() => fallbackOneShot());
    } else if (started) {
      attachStop(started);
    } else {
      fallbackOneShot();
    }

    function fallbackOneShot() {
      // If the stream cannot start at all, one reading is better than none —
      // but it is still marked unsettled, and still judged by the caller.
      if (done || typeof provider.oneShot !== 'function') { finish(false); return; }
      provider.oneShot()
        .then((sample) => { state = considerSample(state, sample, { purpose, target }).state; finish(false); })
        .catch(() => finish(false));
    }
  });
}

/**
 * The school's centre: the same routine, held to the centre standard.
 *
 * Returns `{ fix, usable, source }` rather than throwing on a poor reading,
 * because the coordinate fields stay editable either way — an admin can always
 * type what they read off a map, and the recoverable outcome is the one to
 * offer.
 *
 * `usable` folds in the source: a network estimate is refused as a centre no
 * matter how flattering its reported accuracy, which is the one check that
 * stops a desktop browser's wifi lookup being written down as a school's
 * surveyed position — the failure that survives every other fix in this file,
 * because it corrupts the data rather than the reading.
 */
export async function captureCentreFix(provider, geofenceRadius, onProgress) {
  const fix = await captureBestFix(provider, { purpose: 'centre', onProgress });
  return {
    fix,
    usable: fixIsUsableAsCentre(fix.accuracy, geofenceRadius) && fix.source !== 'network',
    source: fix.source,
  };
}

/** Re-exported so a caller needs one import for the whole capture story. */
export { FIX_WINDOW_MS, GOOD_FIX_METRES, MIN_SAMPLES, TARGET_ACCURACY_M, USELESS_ACCURACY_M };
